<template>
  <div class="pb-16">
    <!-- The slate's state and the clock, then your five, then the controls.
         Only the controls follow you down; the rest scrolls away. -->
    <div class="pt-4">
      <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-semibold text-slate-400">
        <span
          v-if="linesPill"
          class="inline-flex h-6 items-center gap-1.5 rounded-full px-2.5"
          :class="linesPill.tone"
        >
          <span class="h-1.5 w-1.5 rounded-full" :class="linesPill.dot" aria-hidden="true"></span>
          {{ linesPill.label }}
        </span>
        <!-- How old the market is, said once for the slate: the books are
             polled together, so every card would print the same time. -->
        <span
          v-if="booksAge"
          :class="booksAge.stale ? 'font-bold text-amber-300' : ''"
          :title="booksAge.title"
        >
          {{ booksAge.label }}
        </span>
      </div>

      <div class="mt-3 flex items-end justify-between gap-3">
        <div class="flex min-w-0 items-center gap-2.5">
          <h1 class="text-[44px] font-black leading-none tracking-tight text-white">
            Week <span class="text-brand-green">{{ currentWeek || "—" }}</span>
          </h1>
          <!-- Admin-only hand-pull of the market, here rather than only on
               Data health so a stale line can be refreshed from the screen
               that shows it. Spends Odds API credits on every tap. -->
          <button
            v-if="authState.user && authState.user.admin"
            type="button"
            class="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-slate-400 transition-colors hover:bg-white/[0.09] hover:text-slate-200 disabled:opacity-50"
            :disabled="refreshing"
            :aria-label="refreshing ? 'Fetching fresh lines' : 'Fetch fresh lines'"
            :title="refreshing ? 'Fetching fresh lines…' : 'Fetch fresh lines (spends Odds API credits)'"
            @click="refreshLines"
          >
            <svg
              class="h-4 w-4"
              :class="{ 'animate-spin': refreshing }"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <polyline points="21 3 21 9 15 9" />
            </svg>
          </button>
          <!-- Pick the top 5, then keep following it as the lines move.
               Grey like the refresh beside it until pressed; solid green
               while your app picks follow the top five. Gone once the
               deadline has passed: nothing is left to follow. -->
          <button
            v-if="canPickTopFive"
            type="button"
            class="grid h-9 w-9 shrink-0 place-items-center rounded-full text-base font-black leading-none transition-colors disabled:opacity-50"
            :class="following
              ? 'bg-brand-green text-brand-navy hover:bg-emerald-300'
              : 'border border-white/10 bg-white/[0.04] text-slate-400 hover:bg-white/[0.09] hover:text-slate-200'"
            :disabled="topFiveBusy"
            :aria-pressed="following ? 'true' : 'false'"
            :aria-label="following ? 'Following the top 5 — tap to stop' : 'Pick the top 5'"
            :title="following ? 'Following the top 5 — tap to stop (your picks stay)' : 'Pick the top 5'"
            @click="toggleTopFive"
          >
            5
          </button>
        </div>
        <div v-if="deadline" class="shrink-0 text-right">
          <p class="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Pool deadline</p>
          <p class="mt-0.5 text-sm font-bold text-white">{{ deadline.label }}</p>
          <p
            class="mt-0.5 font-mono text-xs font-semibold tabular-nums"
            :class="deadline.closed ? 'text-slate-400' : 'text-amber-300'"
          >
            {{ deadline.countdown }}
          </p>
        </div>
      </div>

      <!-- Inline rather than a toast: it belongs next to the slate it just
           changed, and a toast on a phone covers the first card. -->
      <p
        v-if="refreshResult"
        class="mt-2 text-xs font-semibold"
        :class="refreshResult.ok ? 'text-brand-green' : 'text-amber-300'"
      >
        {{ refreshResult.message }}
      </p>
    </div>

    <!-- Your five: on top once you start picking, never filtered by the
         search below. Hidden until the first pick -- an empty card is space
         spent on nothing while you are still reading the board. Each pick is
         a one-line row that opens into its full game card. -->
    <section
      v-if="!loading && myPicks.length"
      class="mt-5 rounded-2xl border p-3 shadow-lg shadow-black/15"
      :class="atPickLimit ? 'border-brand-green/40 bg-brand-navy/50' : 'border-white/10 bg-brand-navy/40'"
      aria-labelledby="your-five-heading"
    >
      <header class="flex items-center justify-between gap-3 px-0.5">
        <div class="flex min-w-0 items-baseline gap-2">
          <h2
            id="your-five-heading"
            class="text-[11px] font-extrabold uppercase tracking-[0.14em] text-brand-green"
          >
            Your five
          </h2>
          <span
            class="font-mono text-xs font-bold tabular-nums"
            :class="overPickLimit ? 'text-rose-400' : atPickLimit ? 'text-brand-green' : 'text-slate-400'"
          >
            {{ picksMade }}/5
          </span>
          <span
            v-if="overPickLimit"
            class="text-[10px] font-bold uppercase tracking-[0.1em] text-rose-400"
          >
            Trim to 5
          </span>
        </div>
        <!-- The email itself is composed on Picks, which has the sender and
             the fallbacks; this is the way there. -->
        <router-link
          to="/picks"
          class="inline-flex h-9 shrink-0 items-center rounded-xl px-3.5 text-[13px] font-bold transition-colors"
          :class="atPickLimit || emailUpdate
            ? 'bg-brand-green text-brand-navy hover:bg-emerald-300'
            : 'border border-white/10 text-slate-300 hover:bg-white/[0.05]'"
        >
          {{ emailUpdate ? "Email update" : atPickLimit ? "Email picks" : "Review" }}
        </router-link>
      </header>

      <!-- What the top-five following just changed, with a way back. Undo
           restores the old pick as your own, so the next refresh leaves it. -->
      <div
        v-if="swapNotice"
        class="mt-2.5 flex items-center gap-2.5 rounded-xl border border-amber-300/30 bg-amber-300/[0.06] py-2 pl-3 pr-1.5"
      >
        <div class="min-w-0 flex-1">
          <p class="text-xs font-extrabold text-amber-300">{{ swapNotice.title }}</p>
          <p class="mt-0.5 text-[11px] leading-snug text-slate-300">{{ swapNotice.detail }}</p>
        </div>
        <button
          type="button"
          class="h-9 shrink-0 rounded-[10px] border border-white/15 px-3 text-xs font-bold text-slate-200 transition-colors hover:bg-white/[0.05] disabled:opacity-50"
          :disabled="topFiveBusy"
          @click="undoSwap"
        >
          Undo
        </button>
      </div>

      <div class="mt-2">
        <div
          v-for="(game, index) in myPicks"
          :key="game.id"
          class="space-y-2 border-b border-white/[0.07] py-0.5 last:border-b-0"
        >
          <PickedGameRow
            :game="game"
            :position="index + 1"
            :tag="pickTag(game)"
            :expanded="isPickedGameExpanded(game.id)"
            @toggle="togglePickedGame(game.id)"
            @remove="handlePick(game, game.pick.side)"
          />
          <GameCard
            v-if="isPickedGameExpanded(game.id)"
            :id="`game-${game.id}`"
            :game="game"
            :pickable="true"
            :picked-side="game.pick.side"
            :highlighted="String(game.id) === focusGameId"
            @pick="(side) => handlePick(game, side)"
          />
        </div>
      </div>

      <p v-if="emailUpdate" class="mt-2 px-0.5 text-[11px] leading-snug text-amber-300">
        {{ emailUpdate }}
      </p>
    </section>

    <!-- Sticky rather than fixed; the offset comes from `--app-header-h`
         (App.vue). Sort tabs by default; the search field takes their place
         while it is open. One rule, under the tabs: it separates the controls
         from the cards rather than the header from the controls. Once stuck
         it gains a shadow for the cards scrolling beneath it. -->
    <div
      ref="filters"
      class="filters sticky z-40 -mx-4 mb-4 mt-3 border-b border-white/10 bg-brand-bg px-4 pb-3.5 pt-2 transition-shadow sm:-mx-6 sm:px-6"
      :class="filtersStuck ? 'shadow-lg shadow-black/20' : ''"
    >
      <div class="flex items-center gap-2">
        <div
          v-if="!searchOpen"
          role="tablist"
          aria-label="Sort games"
          class="grid min-w-0 flex-1 grid-cols-3 rounded-xl border border-white/10 bg-brand-navy/40 p-[3px]"
        >
          <button
            v-for="tab in sortTabs"
            :key="tab.value"
            type="button"
            role="tab"
            :aria-selected="sortOption === tab.value"
            class="h-9 rounded-[9px] text-[12.5px] font-bold transition-colors"
            :class="sortOption === tab.value ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-slate-200'"
            @click="sortOption = tab.value"
          >
            {{ tab.label }}
          </button>
        </div>
        <input
          v-else
          ref="search"
          v-model="searchQuery"
          type="text"
          placeholder="Search teams"
          aria-label="Search teams"
          class="block h-[44px] min-w-0 flex-1 rounded-xl border border-slate-600/70 bg-slate-800/55 px-3 text-base text-white placeholder-slate-400 focus:border-emerald-500/60 focus:outline-none focus:ring-1 focus:ring-emerald-500/40"
        />
        <button
          type="button"
          class="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-white/10 bg-brand-navy/40 text-slate-300 transition-colors hover:text-white"
          :aria-label="searchOpen ? 'Close search' : 'Search teams'"
          @click="toggleSearch"
        >
          <svg
            v-if="searchOpen"
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
          <svg
            v-else
            class="h-[18px] w-[18px]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex flex-col items-center justify-center py-20">
      <div
        class="h-10 w-10 animate-spin rounded-full border-4 border-slate-700 border-t-emerald-500"
      ></div>
      <p class="mt-4 text-base text-slate-400">Loading games…</p>
    </div>

    <div
      v-else-if="error"
      class="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-300"
    >
      {{ error }}
    </div>

    <!-- A pick failing shouldn't blank out the list underneath it, unlike a
         load failure -- so this isn't part of the v-else-if chain above. -->
    <p
      v-if="!loading && !error && pickError"
      class="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300"
    >
      {{ pickError }}
    </p>

    <!-- Empty -->
    <div v-else-if="!spreads.length" class="py-20 text-center">
      <div
        class="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-slate-800"
      >
        <svg
          class="h-10 w-10 text-slate-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0H4"
          />
        </svg>
      </div>
      <p class="mb-1 text-lg text-slate-300">No games available</p>
      <p class="text-sm text-slate-500">
        Check back later for updated game schedules
      </p>
    </div>

    <!-- No search results -->
    <div
      v-else-if="!visibleGames.length"
      class="py-16 text-center text-base text-slate-400"
    >
      No teams match “{{ searchQuery }}”.
    </div>

    <div v-else>
      <div class="space-y-3 sm:space-y-4">
        <!-- Thursday-to-Saturday games lead the list unlabelled: each one's
             decision closes at its own kickoff, before anything ranked below.
             They are on the board, just never ranked (see `ranked`). -->
        <GameCard
          v-for="game in earlyGames"
          :id="`game-${game.id}`"
          :key="game.id"
          :game="game"
          :pickable="true"
          :picked-side="null"
          :highlighted="String(game.id) === focusGameId"
          @pick="(side) => handlePick(game, side)"
        />
        <GameCard
          v-for="game in remainingGames"
          :id="`game-${game.id}`"
          :key="game.id"
          :game="game"
          :pickable="true"
          :picked-side="null"
          :highlighted="String(game.id) === focusGameId"
          @pick="(side) => handlePick(game, side)"
        />
      </div>

      <!-- Played games sit below everything, collapsed: nothing here is a
           decision any more, but the card still holds the result. -->
      <section v-if="finishedGames.length" class="mt-4">
        <button
          type="button"
          class="flex w-full items-center gap-3 rounded-lg py-2 text-left"
          :aria-expanded="showFinished"
          @click="finishedExpanded = !finishedExpanded"
        >
          <span class="h-px flex-1 bg-white/10" aria-hidden="true"></span>
          <span class="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            {{ finishedGames.length }} played
          </span>
          <svg
            class="h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform"
            :class="showFinished ? 'rotate-180' : ''"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
          <span class="h-px flex-1 bg-white/10" aria-hidden="true"></span>
        </button>

        <div v-if="showFinished" class="mt-2 space-y-3 opacity-70 sm:space-y-4">
          <GameCard
            v-for="game in finishedGames"
            :id="`game-${game.id}`"
            :key="game.id"
            :game="game"
            :pickable="true"
            :picked-side="null"
            :highlighted="String(game.id) === focusGameId"
            @pick="(side) => handlePick(game, side)"
          />
        </div>
      </section>
    </div>

    <p
      v-if="!loading && spreads.length"
      class="mt-6 text-center text-sm text-slate-500"
    >
      Showing {{ visibleGames.length }} of {{ spreads.length }} games
    </p>
  </div>
</template>

<script>
import api from "@/utils/api";
import auth from "@/utils/auth";
import GameCard from "../components/GameCard.vue";
import PickedGameRow from "../components/PickedGameRow.vue";
import { edgeMap, searchGames, sortGames } from "../utils/homeSort.js";
import { mapStoredGame } from "../utils/storedGame.js";
import {
  deadlineCountdown,
  decisionDeadline,
  formatDeadline,
  poolDeadline,
  slateMarketAge,
} from "../utils/marketAge.js";
import { isSundayOrMonday } from "../utils/seasonScorecard.js";
import { formatSigned } from "../utils/edge.js";
import { isFollowing, planTopFive } from "../utils/topFive.js";
import { droppedSinceEmail, readEmailed } from "../utils/emailedPicks.js";

// Only Sunday and Monday games are ranked. Thursday-to-Saturday games are on
// the commissioner's board and can be picked before their own kickoff; the
// user just never picks them, so they never compete for the five. A game with
// no kickoff yet is ranked rather than hidden.
function ranked(game) {
  return !game.kickoffAt || isSundayOrMonday(game.kickoffAt);
}

export default {
  name: "HomeView",
  components: { GameCard, PickedGameRow },
  data() {
    return {
      authState: auth.state,
      // Admin-only hand-pull of the market. `refreshResult` is null when idle;
      // otherwise the last outcome, shown inline and cleared on the next tap.
      refreshing: false,
      refreshResult: null,
      searchQuery: "",
      searchOpen: false,
      filtersStuck: false,
      sortOption: "edge",
      sortTabs: [
        { value: "edge", label: "Win %" },
        { value: "value", label: "Line value" },
        { value: "time", label: "Kickoff" },
      ],
      finishedExpanded: false,
      spreads: [],
      loading: true,
      error: null,
      pickError: null,
      expandedPickedGameIds: [],
      // Set from ?focus=<id> when arriving via Picks' "view on Home" link;
      // cleared a few seconds after the scroll-to so the ring is a moment's
      // notice, not a permanent decoration.
      focusGameId: null,
      now: Date.now(),
      clock: null,
      // The slate's season, for the per-device "emailed" record.
      season: null,
      // The per-device emailed record for this slate, re-read on every load
      // (localStorage is not reactive).
      emailed: null,
      // Top-five following: a write in flight, the last automatic swap (for
      // its notice and Undo) and the games it brought in (tagged NEW).
      topFiveBusy: false,
      lastSwap: null,
      newPickIds: [],
      lastLoadedAt: 0,
    };
  },
  computed: {
    currentWeek() {
      return this.spreads.length ? this.spreads[0].week : null;
    },
    // Only games still open to a decision: a played Thursday game that never
    // made the sheet is not a line anyone is waiting on.
    openGames() {
      return this.spreads.filter((g) => !g.completed && !(decisionDeadline(g) <= this.now));
    },
    pendingLines() {
      return this.openGames.filter((g) => g.fivePicksSpread == null).length;
    },
    unconfirmedLines() {
      return this.openGames.filter(
        (g) => g.fivePicksSpread != null && !g.fivePicksConfirmed
      ).length;
    },
    // The slate's line status as one pill: green once every line is frozen,
    // amber while the sheet is still out or awaiting confirmation.
    linesPill() {
      if (!this.spreads.length) return null;
      const total = this.spreads.filter((g) => g.fivePicksSpread != null).length;
      const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
      const amber = { tone: "bg-amber-400/10 text-amber-300", dot: "bg-amber-300" };
      if (total === 0) return { label: "Awaiting the line email", ...amber };
      if (this.pendingLines > 0) return { label: `${plural(this.pendingLines, "line")} pending`, ...amber };
      if (this.unconfirmedLines > 0) return { label: `${this.unconfirmedLines} unconfirmed`, ...amber };
      return {
        label: `${plural(total, "line")} frozen`,
        tone: "bg-brand-green/10 text-brand-green",
        dot: "bg-brand-green shadow-[0_0_8px_#34ef93]",
      };
    },
    deadline() {
      const ms = poolDeadline(this.spreads);
      if (ms == null) return null;
      const countdown = deadlineCountdown(ms, this.now);
      return { label: formatDeadline(ms), countdown, closed: countdown === "Closed" };
    },
    booksAge() {
      return slateMarketAge(this.spreads, this.now);
    },
    // Every game's edge, computed once per change to `spreads`. The ranked
    // list and your five both read it, so neither re-runs the per-book model.
    edges() {
      return edgeMap(this.spreads);
    },
    // Filter first, then sort — the old code sorted by mutating `spreads` in
    // place, which meant the chosen order silently survived into unrelated
    // renders and the "spread" option had to deep-clone a pristine copy back.
    visibleGames() {
      const matched = searchGames(this.spreads, this.searchQuery);
      return sortGames(matched, this.sortOption, this.edges);
    },
    // Every pick, in the board's order, ignoring the search: the card at the
    // top always shows the whole five.
    myPicks() {
      return sortGames(this.spreads.filter((game) => game.pick), this.sortOption, this.edges);
    },
    // A finished game cannot be picked and its edge is decided, so it leaves
    // the ranked list entirely rather than sorting among live games. It is
    // still reachable — the pool settles by email, and a late pick is legal.
    remainingGames() {
      return this.visibleGames.filter((game) => !game.pick && !game.completed && ranked(game));
    },
    // Thursday-to-Saturday games, in kickoff order, shown ABOVE the ranked
    // list with no label: each closes at its own kickoff, first. Never ranked
    // and never Best pick, so their win % cannot compete for the five. Once
    // final they join Played like any other game.
    earlyGames() {
      return this.visibleGames
        .filter((game) => !game.pick && !game.completed && !ranked(game))
        .sort((a, b) => new Date(a.kickoffAt) - new Date(b.kickoffAt));
    },
    // Forced open while searching or deep-linking, so a game the user asked
    // for by name is never hidden inside a collapsed section.
    showFinished() {
      return (
        this.finishedExpanded ||
        Boolean(this.searchQuery) ||
        this.finishedGames.some((game) => String(game.id) === this.focusGameId)
      );
    },
    finishedGames() {
      return this.visibleGames
        .filter((game) => !game.pick && game.completed)
        .sort((a, b) => new Date(a.kickoffAt) - new Date(b.kickoffAt));
    },
    // Computed from the live list rather than the server's picks_made, so a
    // pick or unpick updates the count immediately without another fetch.
    picksMade() {
      return this.spreads.filter((game) => game.pick).length;
    },
    // Five is the pool's number, not a cap: you can hold a shortlist of six
    // or seven candidates and trim it before sending. These just drive the
    // colour and the copy.
    atPickLimit() {
      return this.picksMade === 5;
    },
    // Signed in, with the pool deadline still ahead.
    canPickTopFive() {
      return Boolean(this.authState.user) && Boolean(this.deadline) && !this.deadline.closed;
    },
    following() {
      return isFollowing(this.spreads, this.now);
    },
    swapNotice() {
      const swap = this.lastSwap;
      if (!swap || !this.following) return null;
      const label = ({ game, side }) => `${this.teamAbbr(game, side)} ${this.pickLine(game, side)}`;
      const gain = swap.swaps.reduce((sum, s) => sum + s.gain, 0);
      return {
        title: `Top 5 changed at ${this.clockTime(swap.at)}`,
        detail:
          swap.swaps.map((s) => `${label(s.out)} out, ${label(s.in)} in`).join("; ") +
          ` · +${gain.toFixed(1)}% win chance`,
      };
    },
    // Teams you emailed this week that are no longer in your picks.
    emailUpdate() {
      if (!this.picksMade || !this.deadline || this.deadline.closed) return null;
      const emailed = this.emailed;
      const teams = this.spreads
        .filter((game) => game.pick)
        .map((game) => this.teamAbbr(game, game.pick.side));
      const dropped = droppedSinceEmail(emailed, teams);
      if (!dropped.length) return null;
      return `You emailed at ${this.clockTime(emailed.at)} with ${dropped.join(", ")}. Send the update before the deadline.`;
    },
    overPickLimit() {
      return this.picksMade > 5;
    },
  },
  watch: {
    // Searching or re-sorting from halfway down the list would otherwise leave
    // you parked in the middle of whatever result happened to land there. The
    // controls follow you down the page, so the results have to come back up
    // to meet them.
    searchQuery() {
      this.scrollToResults();
    },
    sortOption() {
      this.scrollToResults();
    },
  },
  methods: {
    // The bar is stuck once its top meets the app header's bottom.
    updateFiltersStuck() {
      const bar = this.$refs.filters;
      const header = document.querySelector(".app-header");
      if (!bar) return;
      this.filtersStuck = bar.getBoundingClientRect().top <= (header?.offsetHeight || 0) + 1;
    },
    // Closing the search clears it: a filter you can no longer see would
    // silently hide games from the board.
    toggleSearch() {
      if (this.searchOpen) {
        this.searchOpen = false;
        this.searchQuery = "";
        return;
      }
      this.searchOpen = true;
      this.$nextTick(() => this.$refs.search?.focus());
    },
    scrollToResults() {
      if (window.scrollY === 0) return;
      // Instant, not smooth: this fires on every keystroke, and animating each
      // one turns a three-letter search into a fight with the scroll position.
      window.scrollTo({ top: 0, behavior: "auto" });
    },
    mapGame(game) {
      return mapStoredGame(game);
    },
    isPickedGameExpanded(gameId) {
      return this.expandedPickedGameIds.includes(String(gameId));
    },
    togglePickedGame(gameId) {
      const id = String(gameId);
      this.expandedPickedGameIds = this.isPickedGameExpanded(id)
        ? this.expandedPickedGameIds.filter((expandedId) => expandedId !== id)
        : [...this.expandedPickedGameIds, id];
    },
    // Same upsert-by-game logic Picks.vue uses: clicking the already-picked
    // side removes the pick, any other side (re)picks it.
    // Scrolls to and briefly rings the game named by ?focus=<id>. Runs after
    // the fetch, not in mounted() directly, because the card doesn't exist to
    // scroll to until `spreads` has rendered.
    //
    // Plain scrollIntoView({block: "center"}) isn't enough here: the fixed
    // app header and this view's own sticky search/sort bar both sit on top
    // of the content rather than pushing it down, so centering the card in
    // the viewport can still tuck its top edge behind them. Measuring both
    // bars' actual rendered height (not a guessed constant, since it can
    // change with the breakpoint) and scrolling past that is what actually
    // clears them.
    applyFocus(gameId) {
      if (!gameId) return;
      const id = String(gameId);
      this.focusGameId = id;
      const focusedGame = this.spreads.find((game) => String(game.id) === id);
      if (focusedGame?.pick && !this.isPickedGameExpanded(id)) {
        this.expandedPickedGameIds = [...this.expandedPickedGameIds, id];
      }
      this.$nextTick(() => {
        const el = document.getElementById(`game-${id}`);
        if (!el) return;
        const header = document.querySelector(".app-header");
        const covered = (header?.offsetHeight || 0) + (this.$refs.filters?.offsetHeight || 0);
        const target = window.scrollY + el.getBoundingClientRect().top - covered - 12;
        window.scrollTo({ top: Math.max(target, 0), behavior: "smooth" });
      });
      window.setTimeout(() => {
        if (this.focusGameId === id) this.focusGameId = null;
      }, 2500);
    },
    teamAbbr(game, side) {
      return side === "home" ? game.homeTeam.abbr : game.awayTeam.abbr;
    },
    pickLine(game, side) {
      const home = game.fivePicksSpread;
      if (home == null) return "";
      return formatSigned(side === "home" ? home : -home);
    },
    clockTime(ms) {
      return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    },
    // While following: NEW on a game the last swap brought in, YOURS on a
    // pick the following will never move.
    pickTag(game) {
      if (!this.following || !game.pick) return null;
      if (this.newPickIds.includes(game.id)) return "new";
      return game.pick.source === "app" ? null : "yours";
    },
    // The game as it is in the slate now: a reload while a write is in flight
    // replaces `spreads`, and a write to the old object would never render.
    current(game) {
      return this.spreads.find((g) => g.id === game.id) ?? game;
    },
    async savePick(game, side, source) {
      const response = await api.post("/api/v1/picks", { game_id: game.id, side, source });
      this.current(game).pick = response.data;
    },
    async dropPick(game) {
      const live = this.current(game);
      if (!live.pick) return;
      await api.delete(`/api/v1/picks/${live.pick.id}`);
      live.pick = null;
    },
    planFor(mode) {
      return planTopFive(this.spreads, { edges: this.edges, now: Date.now(), isRanked: ranked, mode });
    },
    async applySwaps(swaps) {
      // Save the incoming pick first: if it fails, the old one is still there
      // rather than leaving four picks that following never refills.
      for (const swap of swaps) {
        await this.savePick(swap.in.game, swap.in.side, "app");
        if (swap.out.game.id !== swap.in.game.id) await this.dropPick(swap.out.game);
      }
    },
    // The 5 button. Off: pick the top five into every slot your own picks
    // leave. On: stop following -- the app's picks become yours, none removed.
    async toggleTopFive() {
      if (this.topFiveBusy) return;
      this.topFiveBusy = true;
      this.pickError = null;
      try {
        if (this.following) {
          for (const game of this.spreads.filter((g) => g.pick?.source === "app" && !g.pick.submitted_at)) {
            await this.savePick(game, game.pick.side, "hand");
          }
          this.lastSwap = null;
          this.newPickIds = [];
        } else {
          const { adds, swaps } = this.planFor("start");
          if (!adds.length && !swaps.length) {
            this.pickError = "No open slot to fill — your five are all your own picks or already under way.";
            return;
          }
          await this.applySwaps(swaps);
          for (const add of adds) await this.savePick(add.game, add.side, "app");
          this.newPickIds = [];
        }
      } catch (err) {
        this.pickError = err.response?.data?.error || "Couldn't update your picks. Try again.";
      } finally {
        this.topFiveBusy = false;
        this.now = Date.now();
      }
    },
    // After every load: keep the app's picks on the current top five.
    async followTopFive() {
      if (this.topFiveBusy || !this.canPickTopFive || !this.following) return;
      const { swaps } = this.planFor("follow");
      if (!swaps.length) return;
      this.topFiveBusy = true;
      try {
        await this.applySwaps(swaps);
        this.lastSwap = { at: Date.now(), swaps };
        this.newPickIds = swaps.map((s) => s.in.game.id);
      } catch (err) {
        this.pickError = err.response?.data?.error || "Couldn't follow the top 5. Try again.";
      } finally {
        this.topFiveBusy = false;
      }
    },
    // Put the last swap back. The old pick returns as your own, so the next
    // refresh does not swap it straight out again.
    async undoSwap() {
      const swap = this.lastSwap;
      if (!swap || this.topFiveBusy) return;
      this.topFiveBusy = true;
      try {
        for (const s of swap.swaps) {
          await this.savePick(s.out.game, s.out.side, "hand");
          if (s.out.game.id !== s.in.game.id) await this.dropPick(s.in.game);
        }
        this.lastSwap = null;
        this.newPickIds = [];
      } catch (err) {
        this.pickError = err.response?.data?.error || "Couldn't undo that. Try again.";
      } finally {
        this.topFiveBusy = false;
      }
    },
    // Back on the tab after a while: re-read the slate, which re-checks the
    // top five against whatever the books have done since.
    onVisible() {
      if (document.visibilityState !== "visible" || !this.following) return;
      if (Date.now() - this.lastLoadedAt < 2 * 60 * 1000) return;
      this.loadGames();
    },
    async handlePick(game, side) {
      // A top-five write is in flight; a tap now would race it.
      if (this.topFiveBusy) return;
      this.pickError = null;
      const existing = game.pick;

      // A pick on the commissioner's sheet is locked. The backend enforces
      // this too; catching it first just gives a clearer message than the 422.
      if (existing?.submitted_at) {
        this.pickError = "That pick is locked — it's on the commissioner's sheet.";
        return;
      }

      try {
        if (existing && existing.side === side) {
          await api.delete(`/api/v1/picks/${existing.id}`);
          game.pick = null;
          this.expandedPickedGameIds = this.expandedPickedGameIds.filter(
            (id) => id !== String(game.id)
          );
        } else {
          const response = await api.post("/api/v1/picks", {
            game_id: game.id,
            side,
          });
          game.pick = response.data;
        }
      } catch (err) {
        this.pickError =
          err.response?.data?.error || "Couldn't save that pick. Try again.";
      }
    },

    // The week's games, re-read from the API.
    //
    // Extracted from mounted() so the admin refresh can reuse it: this is a
    // single-page app, so replacing `spreads` re-renders every card in place.
    // Reloading the document instead would throw away scroll position, the
    // search box and the sort — and re-download the bundle to show the same
    // page.
    loadGames({ canonicalise = false } = {}) {
      this.error = null;
      const query = this.$route.query;
      const params = {};
      if (query.season) params.season = query.season;
      if (query.week) params.week = query.week;
      if (query.season_type) params.season_type = query.season_type;

      return api
        .get(`/api/v1/games`, { params })
        .then((response) => {
          if (response.data.games && Array.isArray(response.data.games)) {
            this.spreads = response.data.games.map(this.mapGame);
            this.season = response.data.season ?? null;
            this.emailed = readEmailed(this.season, response.data.season_type, response.data.week);
            this.lastLoadedAt = Date.now();
            this.followTopFive();
            // `loading` has to flip before applyFocus's $nextTick runs, not
            // after (a trailing .finally() used to set it): that nextTick
            // resolves as soon as the *currently* pending reactive changes are
            // flushed, and if `loading` were still true in that flush, the
            // game list is still hidden behind the spinner and there's no card
            // to find yet. Setting it here keeps both changes in one flush.
            this.loading = false;
            if (!canonicalise) return;
            // Reflect the resolved slate in the URL so a bare "/" load becomes
            // shareable and reload-safe. Home has no selector of its own, so
            // this only ever needs to run once, right after the initial load.
            // `focus` rides along so a refresh on a deep link still re-scrolls.
            const canonicalQuery = {
              season: String(response.data.season),
              week: String(response.data.week),
              season_type: response.data.season_type,
            };
            if (query.focus) canonicalQuery.focus = query.focus;
            this.$router.replace({ query: canonicalQuery });
            this.applyFocus(query.focus);
          } else {
            console.error("Unexpected data format:", response.data);
            this.error = "Couldn't load this week's games.";
            this.loading = false;
          }
        })
        .catch((error) => {
          console.error("There was an error!", error);
          this.error =
            error.response?.data?.error || "Couldn't load this week's games.";
          this.spreads = [];
          this.loading = false;
        });
    },

    // Force a paid pull of the market, then re-render from it.
    //
    // Admin-only and deliberately not guarded by a confirm step: the whole
    // point is to get a new number without leaving the page. It spends 1–2
    // Odds API credits per tap and is still bounded by the monthly call cap —
    // past the cap the endpoint serves the last stored snapshot and says so
    // rather than spending.
    async refreshLines() {
      if (this.refreshing) return;
      this.refreshing = true;
      this.refreshResult = null;
      try {
        const { data } = await api.post("/api/v1/admin/odds/refresh");
        await this.loadGames();
        if (data.stale) {
          this.refreshResult = { ok: false, message: "Budget cap reached — showing the last stored snapshot." };
        } else if (data.cached) {
          this.refreshResult = { ok: true, message: `Already current — ${data.games} games, no credit spent.` };
        } else {
          const wrote = data.stored ?? 0;
          this.refreshResult = {
            ok: true,
            message: wrote ? `Updated — ${wrote} new book lines.` : "Updated — no book had moved.",
          };
        }
      } catch (error) {
        this.refreshResult = {
          ok: false,
          message: error.response?.data?.error || "Refresh failed.",
        };
      } finally {
        this.refreshing = false;
        // Long enough to read on a phone, short enough not to linger over the
        // slate it is describing.
        setTimeout(() => { this.refreshResult = null; }, 6000);
      }
    },
  },
  mounted() {
    // `canonicalise` only on the first load: it rewrites the URL to the
    // resolved slate and honours ?focus=. A hand-refresh re-reads the same
    // week and must not touch either.
    this.loadGames({ canonicalise: true });
    // Re-read the clock so the books' age turns amber, or into closing
    // lines, while the page sits open.
    window.addEventListener("scroll", this.updateFiltersStuck, { passive: true });
    document.addEventListener("visibilitychange", this.onVisible);
    this.clock = setInterval(() => {
      this.now = Date.now();
    }, 30 * 1000);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.updateFiltersStuck);
    document.removeEventListener("visibilitychange", this.onVisible);
    clearInterval(this.clock);
  },
};
</script>

<style scoped>
.filters {
  /* Exactly the header's height, minus a hair: at fractional device pixel
     ratios a flush `top` still leaves a sliver of scrolling content showing
     between the two bars. Sliding one pixel under the header (z-50) cannot
     be seen; the gap could. */
  top: calc(var(--app-header-h) - 1px);
}
</style>
